"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Globe as Globe2, Languages, MapPin, TrendingUp, ArrowRight, CircleCheck as CheckCircle2, ShieldCheck, Layers } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const uaePillars = [{
  title: "Bilingual Arabic & English Search Architecture",
  description: "I map dual-language search behaviors, ensuring your technical hreflang structure, metadata, and localized content capture high-intent English expat and Arabic commercial queries without cannibalisation.",
  icon: Languages,
  badge: "Language Targeting"
}, {
  title: "Dubai & Emirates Commercial Intent Mapping",
  description: "Search visibility across the UAE requires precision. I build tailored topical hubs targeting specific free zones, corporate hubs like DIFC and Business Bay, and high-value consumer demographics across Abu Dhabi and Dubai.",
  icon: MapPin,
  badge: "Localized Hubs"
}, {
  title: "GCC Cross-Border Expansion & Authority",
  description: "I design search strategies that scale seamlessly from the UAE into key regional markets including Saudi Arabia, Qatar, and Oman with cohesive international domain structures and localized entity signals.",
  icon: Globe2,
  badge: "Regional Scale"
}, {
  title: "High-Competition SERP Strategy for B2B & Retail",
  description: "From high-ticket B2B lead generation to high-volume UAE ecommerce platforms, I implement technical speed optimizations, structured schema, and authentic link acquisition that win competitive terms.",
  icon: TrendingUp,
  badge: "Technical & Content"
}];
const directBenefits = ["Direct communication with me on every strategy session, audit, and implementation.", "Custom Arabic-English keyword architectures tailored to UAE buying behaviors.", "No junior account handlers, opaque retainer fees, or generic agency templates.", "Technical precision across ccTLDs, subdirectories, and Middle East CDN routing."];
export default function RegionalStrategy() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="regionaluaestrategy" data-nav="light" className="bg-background text-foreground py-20 md:py-28 relative overflow-hidden">
      {/* Decorative background accents */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-24 right-0 size-96 rounded-full bg-secondary/40 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute bottom-0 left-[-10%] size-80 rounded-full bg-accent/10 blur-3xl" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="max-w-3xl mb-14 md:mb-20">
          <Badge variant="outline" className="mb-4 border-primary/20 bg-secondary/50 text-primary font-medium px-3.5 py-1 text-xs">
            Regional & GCC Search Strategy
          </Badge>

          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground leading-[1.15]">
            Tailored SEO Strategy for Dubai and the Broader UAE Market
          </h2>

          <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Expanding into the UAE demands more than translated content. I build bespoke search 
            strategies designed specifically for the unique multilingual dynamics, rapid market growth, 
            and aggressive search landscape of Dubai, Abu Dhabi, and the wider GCC region.
          </p>
        </div>

        {/* 4 Pillars Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-16">
          {uaePillars.map((pillar, i) => {
          const Icon = pillar.icon;
          return <motion.div key={pillar.title} variants={itemVariants} data-index={i}>
                <Card className="h-full border border-border/80 bg-card/60 shadow-sm transition-[border-color,box-shadow] duration-300 hover:border-primary/40 hover:shadow-md">
                  <CardHeader className="gap-3 pb-3">
                    <div className="flex items-center justify-between">
                      <div className="size-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                        <Icon className="size-5" />
                      </div>
                      <span className="text-xs font-semibold text-primary bg-secondary/60 px-2.5 py-1 rounded-md">
                        {pillar.badge}
                      </span>
                    </div>
                    <CardTitle className="font-serif text-xl sm:text-2xl font-semibold text-foreground pt-2">
                      {pillar.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                      {pillar.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Deep Dive & Direct Consultant Section */}
        <div className="rounded-2xl border border-border bg-card p-6 sm:p-10 lg:p-12 shadow-sm">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Left Column: Direct Engagement */}
            <div className="lg:col-span-7 space-y-6">
              <div className="inline-flex items-center gap-2 text-xs font-semibold text-primary uppercase tracking-wider">
                <ShieldCheck className="size-4 text-primary" />
                <span>Hands-On Consultancy</span>
              </div>

              <h3 className="font-serif text-2xl sm:text-3xl font-bold text-foreground leading-tight">
                You Work Directly With Me — Built for Measurable Market Share
              </h3>

              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                Whether you are a UK business establishing regional presence in the Emirates, or a UAE-based 
                enterprise looking to capture regional GCC demand, I handle your search architecture from 
                technical audits to multilingual content roadmaps personally.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                {directBenefits.map((benefit, i) => <div key={i} data-index={i} className="flex items-start gap-2.5 text-sm text-foreground">
                    <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                    <span className="leading-snug">{benefit}</span>
                  </div>)}
              </div>
            </div>

            {/* Right Column: CTA & Quick Navigation */}
            <div className="lg:col-span-5 rounded-xl bg-background border border-border p-6 sm:p-8 flex flex-col justify-between space-y-6 shadow-xs">
              <div>
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Strategic Capabilities
                </span>
                <h4 className="font-serif text-xl font-bold text-foreground mt-1 mb-3">
                  Ready to Grow Your UAE Search Visibility?
                </h4>
                <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                  Discuss your Middle East expansion, bilingual keyword coverage, or current technical bottlenecks directly with me.
                </p>

                <div className="flex flex-col gap-3">
                  <Button asChild size="lg" className="w-full justify-between">
                    <Link to="/ContactPage">
                      <span>Book a Free Consultation</span>
                      <ArrowRight className="size-4 ml-2" />
                    </Link>
                  </Button>

                  <Button asChild variant="outline" size="lg" className="w-full justify-between">
                    <Link to="/InternationalSeoPage">
                      <span>View International SEO Services</span>
                      <Globe2 className="size-4 ml-2" />
                    </Link>
                  </Button>
                </div>
              </div>

              <Separator className="my-2" />

              <div>
                <span className="text-xs font-medium text-muted-foreground block mb-3">
                  Related Market Strategies:
                </span>
                <div className="flex flex-wrap gap-2">
                  <Link to="/LocalSEOPage" className="inline-flex items-center gap-1.5 text-xs text-foreground bg-secondary/60 hover:bg-secondary px-3 py-1.5 rounded-md transition-colors">
                    <MapPin className="size-3 text-primary" />
                    Local SEO
                  </Link>
                  <Link to="/EcommerceSeoPage" className="inline-flex items-center gap-1.5 text-xs text-foreground bg-secondary/60 hover:bg-secondary px-3 py-1.5 rounded-md transition-colors">
                    <TrendingUp className="size-3 text-primary" />
                    Ecommerce SEO
                  </Link>
                  <Link to="/B2BSeoLeadGenerationPage" className="inline-flex items-center gap-1.5 text-xs text-foreground bg-secondary/60 hover:bg-secondary px-3 py-1.5 rounded-md transition-colors">
                    <Layers className="size-3 text-primary" />
                    B2B Lead Generation
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}